import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { Subject } from 'rxjs';
import { take, takeUntil } from 'rxjs/operators';
import { OrganizationGroupService } from '../../services/organization-group/organization-group.service';
import { Organization, OrganizationGroup, UsersService } from '../../services/users/users.service';
import { UserRoleService } from '../../services/user-role/user-role.service';

@Component({
  selector: 'app-firm-picker-modal',
  templateUrl: './firm-picker-modal.component.html',
  styleUrls: ['./firm-picker-modal.component.scss'],
})
export class FirmPickerModalComponent implements OnInit, OnChanges, OnDestroy {
  @Input() open = false;
  @Output() close = new EventEmitter<void>();

  accessibleGroups: OrganizationGroup[] = [];
  accessibleOrgs: Organization[] = [];
  activeGroup: OrganizationGroup | null = null;
  activeOrg: Organization | null = null;

  selectedGroupId: number | null = null;
  selectedOrgId: number | null = null;

  isSuperAdmin = false;
  isSupervisor = false;
  isConfirming = false;

  private readonly destroy$ = new Subject<void>();

  constructor(
    private readonly organizationGroupService: OrganizationGroupService,
    private readonly usersService: UsersService,
    private readonly userRoleService: UserRoleService,
    private readonly cdr: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    this.isSuperAdmin = this.userRoleService.isSuperAdmin();
    this.isSupervisor = this.userRoleService.isSupervisor();

    this.organizationGroupService.activeOrg$
      .pipe(takeUntil(this.destroy$))
      .subscribe((org) => {
        this.activeOrg = org;
        this.cdr.detectChanges();
      });

    this.organizationGroupService.activeGroup$
      .pipe(takeUntil(this.destroy$))
      .subscribe((group) => {
        this.activeGroup = group;
        this.cdr.detectChanges();
      });
  }

  ngOnChanges(changes: SimpleChanges): void {
    const openChange = changes['open'];
    if (openChange && openChange.currentValue === true && !openChange.previousValue) {
      // Refresh role flags when modal opens (user data may have loaded after component creation)
      this.isSuperAdmin = this.userRoleService.isSuperAdmin();
      this.isSupervisor = this.userRoleService.isSupervisor();

      this.initSelections();
      this.loadData();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  get canConfirm(): boolean {
    if (this.isConfirming) return false;
    if (this.isSuperAdmin) {
      return this.selectedGroupId !== null && this.selectedOrgId !== null;
    }
    return this.selectedOrgId !== null;
  }

  onGroupSelectChange(event: Event): void {
    const select = event.target as HTMLSelectElement;
    const raw = select.value;
    if (raw === '') {
      this.selectedGroupId = null;
      return;
    }
    const groupId = Number(raw);
    if (Number.isNaN(groupId)) return;
    this.selectedGroupId = groupId;
    this.selectedOrgId = null;
    this.accessibleOrgs = [];
    this.loadOrgsForGroup(groupId);
  }

  onOrgSelectChange(event: Event): void {
    const select = event.target as HTMLSelectElement;
    const raw = select.value;
    if (raw === '') {
      this.selectedOrgId = null;
      return;
    }
    const orgId = Number(raw);
    if (Number.isNaN(orgId)) return;
    this.selectedOrgId = orgId;
  }

  onConfirm(): void {
    if (!this.canConfirm) return;

    this.isConfirming = true;
    this.cdr.detectChanges();

    if (this.isSuperAdmin && this.selectedGroupId !== null) {
      const groupId = this.selectedGroupId;
      const orgId = this.selectedOrgId;
      const needsGroupSwitch = this.activeGroup === null || this.activeGroup.GROUP_ID !== groupId;

      if (needsGroupSwitch) {
        this.organizationGroupService
          .switchGroup(groupId)
          .pipe(take(1))
          .subscribe({
            next: (res) => {
              this.activeGroup = res.group;
              this.activeOrg = res.organization ?? null;
              if (orgId !== null && res.organization?.ORGANIZATION_ID !== orgId) {
                this.doSwitchOrg(orgId);
              } else {
                this.finalizeConfirm();
              }
            },
            error: (err: unknown) => {
              console.error('FirmPickerModalComponent: switch group failed', err);
              this.revertSelections();
              this.isConfirming = false;
              this.cdr.detectChanges();
            },
          });
      } else if (orgId !== null) {
        this.doSwitchOrg(orgId);
      } else {
        this.finalizeConfirm();
      }
    } else if (this.selectedOrgId !== null) {
      this.doSwitchOrg(this.selectedOrgId);
    } else {
      this.isConfirming = false;
    }
  }

  onCancel(): void {
    this.close.emit();
  }

  onBackdropClick(): void {
    this.close.emit();
  }

  private doSwitchOrg(orgId: number): void {
    this.organizationGroupService
      .switchOrg(orgId)
      .pipe(take(1))
      .subscribe({
        next: () => {
          this.finalizeConfirm();
        },
        error: (err: unknown) => {
          console.error('FirmPickerModalComponent: switch org failed', err);
          this.revertSelections();
          this.isConfirming = false;
          this.cdr.detectChanges();
        },
      });
  }

  private finalizeConfirm(): void {
    void this.usersService.clearCachedUserAndRefresh().catch((err: unknown) => {
      console.error('FirmPickerModalComponent: refresh user failed', err);
    });
    this.isConfirming = false;
    this.cdr.detectChanges();
    this.close.emit();
  }

  private initSelections(): void {
    this.selectedGroupId = this.activeGroup?.GROUP_ID ?? null;
    this.selectedOrgId = this.activeOrg?.ORGANIZATION_ID ?? null;
  }

  private revertSelections(): void {
    this.selectedGroupId = this.activeGroup?.GROUP_ID ?? null;
    this.selectedOrgId = this.activeOrg?.ORGANIZATION_ID ?? null;
  }

  private loadData(): void {
    if (this.isSuperAdmin) {
      this.loadSuperAdminGroups();
    } else if (this.isSupervisor) {
      this.loadSupervisorOrgs();
    }
  }

  private loadSuperAdminGroups(): void {
    this.organizationGroupService
      .getCachedGroups()
      .pipe(take(1))
      .subscribe({
        next: (groups) => {
          this.accessibleGroups = groups;

          if (this.selectedGroupId !== null) {
            this.loadOrgsForGroup(this.selectedGroupId);
          } else if (this.accessibleGroups.length > 0) {
            const first = this.accessibleGroups[0];
            if (first) {
              this.selectedGroupId = first.GROUP_ID;
              this.loadOrgsForGroup(first.GROUP_ID);
            }
          }

          this.cdr.detectChanges();
        },
        error: (err: unknown) => {
          console.error('FirmPickerModalComponent: failed to load groups', err);
        },
      });
  }

  private loadSupervisorOrgs(): void {
    this.organizationGroupService
      .getCachedAccessibleOrgs()
      .pipe(take(1))
      .subscribe({
        next: (orgs) => {
          this.accessibleOrgs = orgs;
          this.cdr.detectChanges();
        },
        error: (err: unknown) => {
          console.error('FirmPickerModalComponent: failed to load accessible orgs', err);
        },
      });
  }

  private loadOrgsForGroup(groupId: number): void {
    this.organizationGroupService
      .getCachedOrgsForGroup(groupId)
      .pipe(take(1))
      .subscribe({
        next: (orgs) => {
          this.accessibleOrgs = orgs;
          if (this.selectedOrgId === null && this.accessibleOrgs.length > 0) {
            const activeOrgId = this.activeOrg?.ORGANIZATION_ID;
            const match = activeOrgId
              ? (this.accessibleOrgs.find((o) => o.ORGANIZATION_ID === activeOrgId) ?? null)
              : null;
            const firstOrg = this.accessibleOrgs[0];
            this.selectedOrgId = match?.ORGANIZATION_ID ?? firstOrg?.ORGANIZATION_ID ?? null;
          }
          this.cdr.detectChanges();
        },
        error: (err: unknown) => {
          console.error('FirmPickerModalComponent: failed to load orgs for group', err);
        },
      });
  }
}
